import React, { useEffect, useState } from "react";
import { http, money } from "../lib/api";
import { toast } from "sonner";
import { HandHeart, Clock, MapPin, MessageCircle, ShieldCheck, BookOpen } from "lucide-react";

const GENDER = ["Male", "Female", "Prefer not to say"];
const MARITAL = ["Single", "Married", "Divorced", "Widowed", "Prefer not to say"];

export default function Consultation() {
  const [settings, setSettings] = useState(null);
  const [f, setF] = useState({
    // Contact
    name: "", email: "", phone: "",
    // Patient
    patient_name: "", dob: "", age: "", gender: "Male",
    marital_status: "Single", spouse_name: "",
    religion: "", city: "", birth_city: "",
    // Session
    topic: "Treatment with Quran & Sunnah",
    preferred_datetime: "", mode: "in_person", hours: 1, message: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { http.get("/consultation/settings").then((r) => setSettings(r.data)); }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  const setDob = (e) => {
    const dob = e.target.value;
    let age = "";
    if (dob) {
      const d = new Date(dob);
      const diff = new Date(Date.now() - d.getTime());
      age = Math.abs(diff.getUTCFullYear() - 1970);
    }
    setF({ ...f, dob, age });
  };

  const total = settings?.price_per_hour ?? 50;
  const topics = settings?.topics || ["Treatment with Quran & Sunnah"];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/consultation/book", {
        ...f, hours: 1, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-14">
      {/* HERO */}
      <div className="oh-card p-10 lg:p-14 rounded-3xl relative overflow-hidden mb-14">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-amber-500/20 via-lime-500/15 to-teal-500/20 blur-3xl"></div>
        <div className="relative">
          <span className="oh-badge oh-badge-gold inline-flex items-center gap-2" data-testid="cons-badge">
            <BookOpen size={12}/> Private Consultation
          </span>
          <h1 className="font-serif text-5xl sm:text-6xl leading-tight mt-6 mb-4">
            Treatment with <span className="text-lime-400">Qur'an</span> & <span className="text-teal-300">Sunnah</span>
          </h1>
          <p className="text-lg text-white/75 max-w-3xl leading-relaxed">
            A private consultation for those facing the difficulties of daily life — held in the light of the Qur'an and Sunnah, with dignity and confidentiality.
          </p>
          <div className="flex flex-wrap gap-6 mt-8 text-xs font-mono text-white/60 tracking-widest">
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-lime-400"/> CONFIDENTIAL</span>
            <span className="flex items-center gap-2"><Clock size={14} className="text-teal-400"/> 30 – 60 MINUTES</span>
            <span className="flex items-center gap-2"><MapPin size={14} className="text-amber-400"/> BIRMINGHAM · IN-PERSON OR CALL</span>
          </div>
        </div>
      </div>

      {/* Booking form */}
      <div className="grid lg:grid-cols-[1.5fr,1fr] gap-8">
        <form onSubmit={submit} className="oh-card p-8 space-y-6" data-testid="cons-form">
          <div>
            <h2 className="font-serif text-3xl">Treatment with Qur'an & Sunnah</h2>
            <p className="text-sm text-white/60 mt-1">All fields help the mentor prepare for your session.</p>
          </div>

          {/* Contact */}
          <fieldset className="space-y-3">
            <legend className="oh-badge oh-badge-turq">Your Contact</legend>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <input required placeholder="Your full name" className="oh-input" value={f.name} onChange={set("name")} data-testid="cons-name"/>
              <input required type="email" placeholder="Email" className="oh-input" value={f.email} onChange={set("email")} data-testid="cons-email"/>
              <input required placeholder="Phone / WhatsApp" className="oh-input col-span-1 sm:col-span-2" value={f.phone} onChange={set("phone")} data-testid="cons-phone"/>
            </div>
          </fieldset>

          {/* Patient */}
          <fieldset className="space-y-3">
            <legend className="oh-badge oh-badge-lime">Patient Details</legend>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <input required placeholder="Patient name" className="oh-input" value={f.patient_name} onChange={set("patient_name")} data-testid="cons-patient"/>
              <label className="text-xs text-white/60">Date of birth
                <input required type="date" className="oh-input mt-1" value={f.dob} onChange={setDob} data-testid="cons-dob"/>
              </label>
              <label className="text-xs text-white/60">Age
                <input placeholder="Auto-calculated" className="oh-input mt-1" value={f.age} onChange={set("age")} data-testid="cons-age"/>
              </label>
              <label className="text-xs text-white/60">Gender
                <select className="oh-input mt-1" value={f.gender} onChange={set("gender")} data-testid="cons-gender">
                  {GENDER.map((g) => <option key={g}>{g}</option>)}
                </select>
              </label>
              <label className="text-xs text-white/60">Marital status
                <select className="oh-input mt-1" value={f.marital_status} onChange={set("marital_status")} data-testid="cons-marital">
                  {MARITAL.map((m) => <option key={m}>{m}</option>)}
                </select>
              </label>
              {f.marital_status === "Married" && (
                <input placeholder="Spouse's name" className="oh-input self-end" value={f.spouse_name} onChange={set("spouse_name")} data-testid="cons-spouse"/>
              )}
              <input placeholder="Religion" className="oh-input" value={f.religion} onChange={set("religion")} data-testid="cons-religion"/>
              <input placeholder="City you live in" className="oh-input" value={f.city} onChange={set("city")} data-testid="cons-city"/>
              <input placeholder="City of birth" className="oh-input sm:col-span-2" value={f.birth_city} onChange={set("birth_city")} data-testid="cons-birth-city"/>
            </div>
          </fieldset>

          {/* Session */}
          <fieldset className="space-y-3">
            <legend className="oh-badge oh-badge-gold">Session Preferences</legend>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <select className="oh-input" value={f.topic} onChange={set("topic")} data-testid="cons-topic">
                {topics.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="Other">Other</option>
              </select>
              <select className="oh-input" value={f.mode} onChange={set("mode")} data-testid="cons-mode">
                <option value="in_person">In person · Birmingham store</option>
                <option value="phone">Phone / WhatsApp call</option>
              </select>
              <input required type="datetime-local" className="oh-input sm:col-span-2" value={f.preferred_datetime} onChange={set("preferred_datetime")} data-testid="cons-datetime"/>
            </div>
            <textarea rows={3} placeholder="Briefly, what is happening? (optional, confidential)"
              className="oh-input" value={f.message} onChange={set("message")} data-testid="cons-message"/>
          </fieldset>

          <div className="border-t border-[var(--oh-border)] pt-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs font-mono text-white/50 tracking-widest">CONSULTATION FEE</div>
              <div className="font-mono text-2xl text-lime-300">{money(total)}</div>
              <div className="text-xs text-white/60">Covers 30 – 60 minutes · additional charges discussed with the mentor</div>
            </div>
            <button disabled={busy || !settings?.active} className="oh-btn-lime px-6 py-3 rounded-full text-xs tracking-widest uppercase disabled:opacity-50" data-testid="cons-submit">
              {busy ? "Redirecting…" : "Pay & Confirm →"}
            </button>
          </div>
          <p className="text-[11px] text-white/40 text-center">Secured by Stripe. You'll be redirected to complete payment. We'll confirm your slot by email or WhatsApp within one working day.</p>
        </form>

        <aside className="space-y-5">
          <div className="oh-card p-6">
            <span className="oh-badge oh-badge-turq">How it works</span>
            <ol className="mt-4 space-y-3 text-sm text-white/75">
              <li><b className="text-lime-300">01.</b> Complete the patient details</li>
              <li><b className="text-lime-300">02.</b> Choose in-person or phone, pick a time</li>
              <li><b className="text-lime-300">03.</b> Pay the £50 consultation fee (secure Stripe)</li>
              <li><b className="text-lime-300">04.</b> We reach out to confirm the slot</li>
              <li><b className="text-lime-300">05.</b> Meet in Birmingham, or receive a call — treatment is offered in the light of the Qur'an and Sunnah</li>
            </ol>
          </div>
          <div className="oh-card p-6">
            <span className="oh-badge oh-badge-gold">Important</span>
            <p className="text-sm text-white/70 mt-3">
              The £50 fee covers your first 30 – 60 minute consultation. Any additional charges (follow-up sessions, home visits, extended time) will be discussed directly with the mentor.
            </p>
            <p className="text-sm text-white/60 mt-3">
              This service is offered in the spirit of the Qur'an and Sunnah. It is <b>not</b> a substitute for medical, psychological, legal or financial advice. If you have a health concern, please also consult a qualified professional.
            </p>
          </div>
          <a href="https://wa.me/447877855305" target="_blank" rel="noreferrer" className="oh-card p-6 flex items-center gap-4 link-hover" data-testid="cons-wa">
            <MessageCircle size={28} className="text-lime-400"/>
            <div>
              <div className="font-serif text-xl">Question before booking?</div>
              <div className="text-xs text-white/60 font-mono tracking-widest">WHATSAPP +44 7877 855305</div>
            </div>
          </a>
        </aside>
      </div>
    </div>
  );
}
